import { useEffect, useState } from "react";
import { getMyProfile, updateMyProfile, changeMyPassword, submitSupportTicket, getMySupportTickets, reopenSupportTicket } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import LoadingIndicator from "../../components/LoadingIndicator";

const BUSINESS_STATUS_LABEL = {
  none: "Not requested",
  pending: "⏳ Pending review",
  approved: "✓ Approved",
  revoked: "❌ Revoked"
};

export default function Profile() {
  const { updateAccount } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");
  const [profileError, setProfileError] = useState("");

  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [tickets, setTickets] = useState([]);
  const [ticketForm, setTicketForm] = useState({ category: "delivery_issue", subject: "", message: "" });
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketError, setTicketError] = useState("");

  const loadTickets = () => getMySupportTickets().then(r => setTickets(r.data)).catch(() => {});

  useEffect(() => {
    getMyProfile()
      .then(r => {
        setProfile(r.data);
        setForm({ name: r.data.name || "", phone: r.data.phone || "", email: r.data.email || "" });
      })
      .catch(() => setProfileError("Could not load your profile."))
      .finally(() => setLoading(false));
    loadTickets();
  }, []);

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    setTicketError("");
    setTicketMessage("");
    setSubmittingTicket(true);
    try {
      await submitSupportTicket(ticketForm);
      setTicketMessage("✓ Your message has been sent — our support team will get back to you soon.");
      setTicketForm({ category: "delivery_issue", subject: "", message: "" });
      await loadTickets();
    } catch (err) {
      setTicketError(err.response?.data?.error || "Could not send your message.");
    } finally {
      setSubmittingTicket(false);
    }
  };

  const handleReopenTicket = async (ticketId) => {
    setTicketError("");
    try {
      await reopenSupportTicket(ticketId);
      setTicketMessage("✓ Ticket has been re-opened. Support staff has been notified.");
      await loadTickets();
    } catch (err) {
      setTicketError(err.response?.data?.error || "Could not re-open ticket.");
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileMessage("");
    setSavingProfile(true);
    try {
      const { data } = await updateMyProfile(form);
      setProfile(data);
      updateAccount(data);
      setProfileMessage("✓ Profile updated successfully.");
    } catch (err) {
      setProfileError(err.response?.data?.error || "Could not update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordMessage("");
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    setSavingPassword(true);
    try {
      await changeMyPassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordMessage("✓ Password changed successfully.");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setPasswordError(err.response?.data?.error || "Could not change password.");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader
          walletBalance={null}
          membershipTier={profile?.membershipTier || "standard"}
          backTo="/order"
          backLabel="Send Package"
        />

        <div className="page">
          <div className="brand-header">
            <span className="brand-logo">👤</span>
            <span className="brand-name">My Profile</span>
            <p className="brand-tagline">Update your details and password</p>
          </div>

          {loading ? (
            <LoadingIndicator label="Loading profile..." />
          ) : (
            <>
              <form onSubmit={handleProfileSubmit} className="delivery-card" style={{ marginBottom: "24px" }}>
                <h3>Profile Details</h3>
                <div className="form-field">
                  <label>Full Name</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="form-field">
                  <label>Phone Number</label>
                  <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} required />
                </div>
                <div className="form-field">
                  <label>Email Address</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                </div>
                {profileError && <p className="error">{profileError}</p>}
                {profileMessage && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{profileMessage}</p>}
                <button type="submit" disabled={savingProfile}>{savingProfile ? "Saving..." : "Save Profile"}</button>
              </form>

              {/* BUSINESS ACCOUNTS — full applications live on their own pages */}
              <div className="delivery-card" style={{ marginBottom: "24px" }}>
                <h3>🏢 Business Accounts</h3>
                <div className="info-row">
                  <span className="label">🧑‍💼 Agent</span>
                  <span className="value">{BUSINESS_STATUS_LABEL[profile?.agentApprovalStatus || "none"]}</span>
                </div>
                <div className="info-row">
                  <span className="label">🚚 Fleet Owner</span>
                  <span className="value">{BUSINESS_STATUS_LABEL[profile?.fleetOwnerApprovalStatus || "none"]}</span>
                </div>
                <div className="info-row">
                  <span className="label">🏢 B2B / Corporate</span>
                  <span className="value">{BUSINESS_STATUS_LABEL[profile?.b2bApprovalStatus || "none"]}</span>
                </div>
                <Link to="/business">
                  <button type="button" className="secondary" style={{ marginTop: "12px" }}>Explore Business Accounts</button>
                </Link>
              </div>

              <form onSubmit={handlePasswordSubmit} className="delivery-card">
                <h3>Change Password</h3>
                <div className="form-field">
                  <label>Current Password</label>
                  <input type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))} required />
                </div>
                <div className="form-field">
                  <label>New Password</label>
                  <input type="password" minLength={8} value={passwordForm.newPassword} onChange={e => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))} required />
                </div>
                <div className="form-field">
                  <label>Confirm New Password</label>
                  <input type="password" minLength={8} value={passwordForm.confirmPassword} onChange={e => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))} required />
                </div>
                {passwordError && <p className="error">{passwordError}</p>}
                {passwordMessage && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{passwordMessage}</p>}
                <button type="submit" disabled={savingPassword}>{savingPassword ? "Saving..." : "Change Password"}</button>
              </form>

              {/* HELP & SUPPORT CHANNELS (Task 3) */}
              <div className="delivery-card" style={{ marginTop: "24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <h3 style={{ margin: 0 }}>📞 Customer Support &amp; Helpdesk</h3>
                  <span className="status-badge success" style={{ fontSize: "11px" }}>24/7 Available</span>
                </div>
                <p className="hint" style={{ marginBottom: "16px" }}>
                  Have questions, need delivery support, or experiencing issues? Contact our support staff directly:
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "12px", marginBottom: "12px" }}>
                  <a
                    href="tel:+23480032787363"
                    className="quick-card"
                    style={{ textDecoration: "none", padding: "14px", display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <span style={{ fontSize: "24px" }}>📞</span>
                    <div>
                      <strong style={{ display: "block", fontSize: "13px", color: "var(--text-primary)" }}>Telephone Support</strong>
                      <span style={{ fontSize: "12px", color: "var(--text-accent)" }}>+234 800 FASTSEND</span>
                    </div>
                  </a>
                  <a
                    href="mailto:support@fastsend.ng"
                    className="quick-card"
                    style={{ textDecoration: "none", padding: "14px", display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <span style={{ fontSize: "24px" }}>✉️</span>
                    <div>
                      <strong style={{ display: "block", fontSize: "13px", color: "var(--text-primary)" }}>Email Helpdesk</strong>
                      <span style={{ fontSize: "12px", color: "var(--text-accent)" }}>support@fastsend.ng</span>
                    </div>
                  </a>
                  <a
                    href="https://wa.me/2348123456789?text=Hello%20FastSend%20Support%2C%20I%20need%20assistance%20with%20my%20account%20or%20delivery"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="quick-card"
                    style={{ textDecoration: "none", padding: "14px", display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <span style={{ fontSize: "24px" }}>💬</span>
                    <div>
                      <strong style={{ display: "block", fontSize: "13px", color: "var(--text-primary)" }}>Live Chat</strong>
                      <span style={{ fontSize: "12px", color: "var(--success, #16c784)" }}>WhatsApp Live Agent</span>
                    </div>
                  </a>
                </div>
              </div>

              {/* CONTACT SUPPORT */}
              <div className="delivery-card" style={{ marginTop: "24px" }}>
                <h3>📨 Contact Support Ticket</h3>
                <p className="hint" style={{ marginBottom: "12px" }}>
                  Need help with a delivery, a payment, or something not working right? Send us a message and track response here.
                </p>
                <form onSubmit={handleTicketSubmit}>
                  <div className="form-field">
                    <label>Category</label>
                    <select
                      value={ticketForm.category}
                      onChange={e => setTicketForm(f => ({ ...f, category: e.target.value }))}
                    >
                      <option value="delivery_issue">Delivery Issue</option>
                      <option value="payment_issue">Payment / Wallet Issue</option>
                      <option value="account_issue">Account Issue</option>
                      <option value="app_bug">App Bug / Something's Broken</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Subject</label>
                    <input
                      type="text"
                      value={ticketForm.subject}
                      onChange={e => setTicketForm(f => ({ ...f, subject: e.target.value }))}
                      placeholder="Brief summary of your issue"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Message</label>
                    <textarea
                      value={ticketForm.message}
                      onChange={e => setTicketForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="Tell us what happened — include order details if relevant"
                      rows={4}
                      style={{ resize: "vertical" }}
                      required
                    />
                  </div>
                  {ticketError && <p className="error">{ticketError}</p>}
                  {ticketMessage && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{ticketMessage}</p>}
                  <button type="submit" disabled={submittingTicket}>{submittingTicket ? "Sending..." : "Send Message"}</button>
                </form>

                {tickets.length > 0 && (
                  <div style={{ marginTop: "20px" }}>
                    <h3>Your Messages &amp; Support Tickets</h3>
                    {tickets.map(t => (
                      <div key={t._id} className="history-row" style={{ padding: "14px 12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                          <div>
                            <strong>{t.subject}</strong>
                            <p style={{ fontSize: "12px", color: "var(--text-muted)", margin: "2px 0 0" }}>
                              {new Date(t.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <span
                            className={`status-badge ${
                              t.status === "open"
                                ? "warning"
                                : t.status === "in_progress"
                                ? "info"
                                : t.status === "resolved"
                                ? "success"
                                : ""
                            }`}
                            style={{ fontSize: "11px", textTransform: "uppercase" }}
                          >
                            {t.status.replace(/_/g, " ")}
                          </span>
                        </div>
                        <p className="hint" style={{ marginTop: "6px" }}>{t.message}</p>
                        {t.adminResponse && (
                          <div className="quote-box" style={{ marginTop: "8px", background: "var(--accent-glow)" }}>
                            <p style={{ fontSize: "13px", margin: 0 }}><strong>Support Reply:</strong> {t.adminResponse}</p>
                          </div>
                        )}
                        {(t.status === "resolved" || t.status === "closed") && (
                          <div style={{ marginTop: "8px" }}>
                            <button
                              type="button"
                              className="secondary"
                              style={{ padding: "4px 12px", fontSize: "12px", borderRadius: "14px" }}
                              onClick={() => handleReopenTicket(t._id)}
                            >
                              🔄 Re-open Conversation
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
